import type { UseFormReturn } from 'react-hook-form';
import { faApple } from '@fortawesome/free-brands-svg-icons';
import {
  faCheckCircle,
  faHourglassHalf,
  faXmarkCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Disclosure,
  DisclosureButton,
  Labeled,
  TextInputField,
} from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { Trans, useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import type { IOrganizationFormValues } from './OrganizationForm';
import appStoreConnectAddUserScreenshotUrlEn from '~/assets/appStoreConnectAddUserScreenshot.en.png';
import appStoreConnectAddUserScreenshotUrlFr from '~/assets/appStoreConnectAddUserScreenshot.fr.png';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { Link } from '~/components/atoms/Link';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { getAppSettings } from '~/lib/appSettings';

export type IOrganizationFormIosSectionProps = IContainerProps & {
  form: UseFormReturn<IOrganizationFormValues>;
  mode: IFormMode;
};

const styles = stylex.create({
  list: {
    paddingLeft: '1.5rem',
    listStyleType: 'number',
  },
  appStoreConnectAddUserImage: {
    width: 400,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
});

export const OrganizationFormIosSection: React.FC<
  IOrganizationFormIosSectionProps
> = (props) => {
  const { sx, form } = props;
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const appSettings = getAppSettings();
  const teamStatus = form.watch('appleTeamStatus');
  const setupDone = form.watch('appleSetupDone');

  return (
    <Disclosure
      sx={sx}
      defaultExpanded
      trigger={
        <DisclosureButton leadingIcon={<FontAwesomeIcon icon={faApple} />}>
          {t('OrganizationFormIosSection.title')}
        </DisclosureButton>
      }
    >
      <FormSection sub>
        {setupDone ? (
          <Trans
            parent="p"
            i18nKey="OrganizationFormIosSection.setup-valid"
            components={{
              'support-link': (
                <Link to={`mailto:${appSettings.contact.email}`} />
              ),
            }}
          />
        ) : teamStatus !== 'valid' ? (
          <>
            <Trans
              parent="p"
              i18nKey="OrganizationFormIosSection.setup-invalid.p1"
              components={{
                'enroll-link': (
                  <Link to="https://developer.apple.com/programs/enroll/" />
                ),
              }}
            />
            <Trans
              parent="p"
              i18nKey="OrganizationFormIosSection.setup-invalid.p2"
              components={{
                'manage-users-link': (
                  <Link to="https://appstoreconnect.apple.com/access/users" />
                ),
                img: (
                  <img
                    {...stylex.props(styles.appStoreConnectAddUserImage)}
                    src={
                      locale === 'fr'
                        ? appStoreConnectAddUserScreenshotUrlFr
                        : appStoreConnectAddUserScreenshotUrlEn
                    }
                    alt=""
                  />
                ),
              }}
            />
          </>
        ) : null}
        <Labeled
          label={t('OrganizationFormIosSection.field.team-id.label')}
          supportingText={
            <Trans
              parent="div"
              i18nKey="OrganizationFormIosSection.field.team-id.supporting-text"
              components={{
                'account-link': (
                  <Link to="https://developer.apple.com/account/" />
                ),
              }}
            />
          }
          readOnly={setupDone}
        >
          <FormField
            name="appleTeamId"
            control={form.control}
            as={TextInputField}
            forwardError
            placeholder={t('common.example-value', { value: '88FA7CC6BX' })}
            hasError={teamStatus === 'unknown'}
            rules={{
              minLength: 10,
              maxLength: 10,
              validate: (value) => {
                if (
                  typeof value === 'string' &&
                  !/^[a-zA-Z0-9]*$/.test(value)
                ) {
                  return t(
                    'OrganizationFormIosSection.error.invalid-team-id-format',
                  );
                }

                return true;
              },
            }}
            trailingIcon={
              teamStatus === 'valid' ? (
                <FontAwesomeIcon icon={faCheckCircle} />
              ) : teamStatus === 'checking' ? (
                <FontAwesomeIcon icon={faHourglassHalf} />
              ) : teamStatus === 'unknown' ? (
                <FontAwesomeIcon icon={faXmarkCircle} />
              ) : undefined
            }
          />
        </Labeled>

        {teamStatus === 'unknown' ? (
          <ErrorMessage>
            {t('OrganizationFormIosSection.error.team-id-issue.intro')}
            <ol {...stylex.props(styles.list)}>
              <Trans
                parent="li"
                i18nKey="OrganizationFormIosSection.error.team-id-issue.p1"
                components={{
                  'account-link': (
                    <Link to="https://developer.apple.com/account/" />
                  ),
                }}
              />
              <li>{t('OrganizationFormIosSection.error.team-id-issue.p2')}</li>
            </ol>
          </ErrorMessage>
        ) : undefined}
      </FormSection>
    </Disclosure>
  );
};
